import React, { FC, useCallback, useEffect, useState } from 'react';

import { omit } from 'lodash-es';

import {
	ErrorLoadPageIcon,
	EvFilters,
	EvLink,
	EvSection,
	EvSpinner,
	EvTitle,
	IFilterValues,
	SORT_ORDER,
	useSkipLinks
} from '@evinced-private/ui-common';

import {
	MOBILE_SDK_V1_DASHBOARD_PAGE_MAIN_TITLE_ID,
	MOBILE_SDK_V1_DASHBOARD_PAGE_NEW_PLATFORM_LINK_ID
} from 'data-automation-ids/MobileSdkV1DashboardAutomationIds';
import { ContactUsButton } from 'src/common/components/contact-us/ContactUsButton';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { SummaryPane } from 'src/common/components/summary-pane/SummaryPane';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { flattenObject } from 'src/common/helpers/ObjectsHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { getUsageDataTooltipText } from 'src/common/helpers/TooltipContentHelper';
import { SummaryItem } from 'src/common/interfaces/SummaryItem';
import { ProductType } from 'src/common/interfaces/Tenant';
import { LoadErrorMsgLayout } from 'src/common/pages/property/LoadErrorMsgLayout';
import {
	ITableStateContext,
	useTableState
} from 'src/common/providers/dashboard/TableStateProvider';
import { useUserTenant } from 'src/common/providers/userTenantProvider/UserTenantProvider';
import { Logger } from 'src/common/services/Logger';

import { MSDK_SESSIONS_TABLE_ID } from '../../../../common/pages/dashboards-container/dashboard_consts';
import { MSdkTest } from '../../../mobile-common/types/MobileSdkTypes';
// TODO: create a general css file
import { mobileSdkTestsFilterDefinitions } from '../../helpers/MobileSdkConsts';
import {
	assertUserLabelsToData,
	buildTableFilters,
	getFilteredSDKTest,
	getMsdkTestsColumns
} from '../../helpers/MobileSdkTableHelper';
import { NO_SYNCED_TESTS_MSG, NO_SYNCED_TESTS_TITLE } from '../../helpers/MsdkDashboardConsts';
import { buildMSDKSummaryPaneData, buildMSdkTestList } from '../../helpers/MSdkDashboardHelper';
import { getMobileSdkSessions } from '../../services/MobileSdkDashboardService';

import './MSdkDashboardSessionsPage.scss';

export const MSdkDashboardTestsPage: FC = () => {
	const { tenant, getProductIdByType } = useUserTenant();
	const productId = getProductIdByType(ProductType.MOBILE_SDK);

	const tableStateContext: ITableStateContext = useTableState();
	const tableState = tableStateContext.getTableStateById(MSDK_SESSIONS_TABLE_ID);

	const [isLoading, setIsLoading] = useState(true);
	const [allMSdkTests, setAllMSdkTests] = useState<MSdkTest[]>(null);
	const [summaryPaneData, setSummaryPaneData] = useState<SummaryItem[]>([]);
	const [tableFilters, setTableFilters] = useState<IFilterOptions>();
	const [tableData, setTableData] = useState<MSdkTest[]>([]);

	const initialValues = {
		text: { searchValue: tableState?.filters?.searchValue },
		select: omit(tableState?.filters, 'searchValue')
	};

	useSkipLinks(!isLoading);

	const updateFilters = useCallback(
		(appliedFilters: IFilterValues): void => {
			const flatFilters = flattenObject<IFilterOptions>(appliedFilters);
			const filteredSessions = getFilteredSDKTest(allMSdkTests, flatFilters);

			setTableData(filteredSessions);
			tableStateContext.onFilterChange(MSDK_SESSIONS_TABLE_ID, flatFilters);
		},
		[allMSdkTests, tableStateContext]
	);

	const loadData = useCallback(async (): Promise<void> => {
		try {
			// 1. Fetch MSDK Tests Data
			const res = await getMobileSdkSessions({
				tenantId: tenant.id,
				productId
			});
			const msdkTests: MSdkTest[] = assertUserLabelsToData(buildMSdkTestList(res));
			setAllMSdkTests(assertUserLabelsToData(msdkTests));

			if (msdkTests.length > 0) {
				// Build table filters from data
				const filters: IFilterOptions = buildTableFilters(msdkTests);
				setTableFilters(filters);

				// Build table data based on filters
				const filteredSessions = getFilteredSDKTest(msdkTests, tableState?.filters);
				setTableData(filteredSessions);
			}
		} catch (err) {
			Logger.error('failed to load Mobile SDK dashboard data', err);
		}
	}, [tenant.id, productId, tableState?.filters]);

	useEffect(() => {
		loadData().finally(() => setIsLoading(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const lastMonthData: SummaryItem[] = buildMSDKSummaryPaneData(tableData);
		setSummaryPaneData(lastMonthData);
	}, [tableData]);

	if (isLoading) {
		return <EvSpinner />;
	}
	if (allMSdkTests?.length === 0) {
		return (
			<LoadErrorMsgLayout
				mainTitle={NO_SYNCED_TESTS_TITLE}
				secondaryMsg={NO_SYNCED_TESTS_MSG}
				icon={ErrorLoadPageIcon}
				className="dashboard-no-sessions-msg"
			>
				<ContactUsButton product={ProductType.MOBILE_SDK} />
			</LoadErrorMsgLayout>
		);
	}
	return (
		<div className="msdk-sessions-page">
			<div className="mobile-sdk-sessions-header">
				<EvTitle
					titleText="Mobile SDK report"
					headingLevel={1}
					dataAutomationId={MOBILE_SDK_V1_DASHBOARD_PAGE_MAIN_TITLE_ID}
				/>
				<EvLink
					url={RoutesHelper.getMobileSdkDashboardPath()}
					linkText="See new Tests"
					showIcon
					dataAutomationId={MOBILE_SDK_V1_DASHBOARD_PAGE_NEW_PLATFORM_LINK_ID}
				/>
			</div>
			<EvSection ariaLabel="Mobile SDK All Tests Table" skipLinkId="main-section">
				<div className="filter-line">
					{tableFilters && (
						<EvFilters
							filterDefinitions={mobileSdkTestsFilterDefinitions}
							initialValues={initialValues}
							filterOptions={{ userLabels: tableFilters.userLabels }}
							isFilterOptionsLoaded={!isLoading}
							onApply={updateFilters}
							onReset={updateFilters}
						/>
					)}
				</div>

				<EvSection ariaLabel="Mobile SDK last month usage" className="last-month-usage-section">
					<EvTitle
						className="last-month-usage-title"
						titleText="Test insights"
						id="Last month usage title"
						// eslint-disable-next-line react/jsx-no-useless-fragment
						MoreInfoContent={<>{getUsageDataTooltipText('tests')}</>}
					/>
					<EvTitle
						className="last-month-usage-subtitle"
						titleText="Showing data from the last 30 days"
						id="Last month usage title"
						headingLevel={3}
					/>

					<SummaryPane
						items={summaryPaneData}
						className="base-summary-pane"
						ariaLabel="Mobile SDKs monthly Usage summary"
					/>
				</EvSection>

				<div className="table-title-and-actions-container">
					<EvTitle titleText="Uploaded tests" id="Mobile SDK All SeTestsssions" />
				</div>

				<PlatformTable
					tableId={MSDK_SESSIONS_TABLE_ID}
					tableData={tableData}
					totalCount={tableData.length}
					className="mfa-sessions-table"
					persistTableState
					columns={getMsdkTestsColumns()}
					options={{
						keyField: 'testRunId',
						title: 'Mobile SDK Tests',
						caption: 'Mobile SDK Tests table',
						pagination: true,
						dataType: 'tests',
						defaultSorted: [
							{
								dataField: 'createdAt',
								order: SORT_ORDER.DESC
							}
						],
						// TODO: remove once everything done remotely
						localProps: {
							defaultPage: 1,
							defaultSizePerPage: 10
						}
					}}
				/>
			</EvSection>
		</div>
	);
};
